import * as THREE from './three'
import { Audio } from '../nodes/Audio'
import { DoorPortals } from './doorPortals'
import { createSlidingDoorMaterial } from './slidingDoorMaterial'

// Visual doors have no collider. Every client evaluates all replicated players,
// so a remote avatar also keeps the passage open (subject to network latency).
export class SlidingDoors {
  constructor(root, world) {
    this.world = world
    this.doors = []
    const leaves = []
    root.traverse(node => {
      if (node.name === 'mesh' && /^door_logo(?:\.?\d{3})?(?:_collider)?$/.test(node.id) && node._geometry) leaves.push(node)
    })
    const used = new Set()
    for (const first of leaves) {
      if (used.has(first)) continue
      const second = leaves.find(node => node !== first && !used.has(node) &&
        node.parent === first.parent && node.position.distanceTo(first.position) < 0.01 &&
        Math.abs(node.quaternion.dot(first.quaternion)) > 0.999)
      if (!second) continue
      const pair = [first, second].map(node => {
        node._geometry.computeBoundingBox()
        const box = node._geometry.boundingBox
        return { node,
          centerX: (box.min.x + box.max.x) / 2 }
      }).sort((a, b) => a.centerX - b.centerX)
      if (pair[0].centerX === pair[1].centerX) continue
      for (let index = 0; index < pair.length; index++) {
        const leaf = pair[index]
        leaf.sign = index === 0 ? -1 : 1
        leaf.slide = { value: 0 }
        const material = createSlidingDoorMaterial(leaf.node._material, leaf.slide)
        // Keep bounds covering the complete slide, without rebuilding them per frame.
        const geometry = leaf.node._geometry.clone()
        geometry.computeBoundingBox()
        geometry.boundingBox.min.x -= 1.6 / Math.abs(leaf.node.scale.x)
        geometry.boundingBox.max.x += 1.6 / Math.abs(leaf.node.scale.x)
        geometry.boundingSphere = geometry.boundingBox.getBoundingSphere(new THREE.Sphere())
        leaf.node.geometry = geometry
        leaf.node.material = material
        leaf.node.castShadow = false
      }
      used.add(first)
      used.add(second)
      const sound = new Audio({
        src: '/audio/sci-fi-door-14782.mp3', group: 'sfx', spatial: true,
        volume: 0.65, loop: false, distanceModel: 'linear',
        refDistance: 2, maxDistance: 14, rolloffFactor: 1,
        position: first.position.clone(),
      })
      if (sound) first.parent.add(sound)
      this.doors.push({ leaves: pair, sound, motion: 0, center: new THREE.Vector3().setFromMatrixPosition(first.matrixWorld),
        amount: 0, hold: 0, open: false })
    }
    this.portals = new DoorPortals(world, this.doors, root)
  }

  update(delta) {
    this.portals.update()
    for (const door of this.doors) {
      let nearby = false
      const radius = door.open ? 3.8 : 3
      for (const player of this.world.entities.players.values()) {
        const position = player.base?.position
        if (!position) continue
        const dx = position.x - door.center.x
        const dz = position.z - door.center.z
        if (dx * dx + dz * dz <= radius * radius && Math.abs(position.y - door.center.y) < 4) {
          nearby = true
          break
        }
      }
      if (nearby) door.hold = 1.5
      else door.hold = Math.max(0, door.hold - delta)
      door.open = nearby || door.hold > 0
      const target = door.open ? 1 : 0
      const motion = Math.sign(target - door.amount)
      if (motion !== door.motion) {
        if (motion) door.sound.play(true)
        else door.sound.stop()
        door.motion = motion
      }
      const next = THREE.MathUtils.clamp(door.amount + Math.sign(target - door.amount) * delta / 0.8, 0, 1)
      if (next === door.amount) continue
      door.amount = next
      const travel = 1.6 * next * next * (3 - 2 * next)
      for (const leaf of door.leaves) leaf.slide.value = leaf.sign * travel / leaf.node.scale.x
    }
  }
}
